import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { REFERRER_TYPE } from '../../../constants/gaEnums';
import { useSearchParams } from 'react-router-dom';
import { Box, Divider, Grid, Stack, useMediaQuery } from '@mui/material';
import { ViewListRounded, ViewModuleRounded } from '@mui/icons-material';
import theme from '../../../theme';
import MyReviewCard from '../items/MyReviewCard';
import MyReviewVerticalCard from '../items/MyReviewVerticalCard';
import Pagination from '../../../components/common/Pagination';
import ProgressBar from '../../../components/common/ProgressBar';
import NoData from '../../../components/common/NoData';
import SubTab from '../../../components/button/SubTab';
import SelectFilter from '../../../components/common/SelectFilter';
import ViewModeToggle from '../../../components/common/ViewModeToggle';

const SUB_TABS = [
  { label: '앨범', key: 'albums' },
  { label: '싱글', key: 'singles' },
];

const SORT_OPTIONS = [
  { label: '최신순', value: 'latest' },
  { label: '좋아요순', value: 'likes' },
  { label: '높은 평점순', value: 'rating_high' },
  { label: '낮은 평점순', value: 'rating_low' },
];

const VIEW_MODES = [
  { value: 'list', Icon: ViewListRounded },
  { value: 'grid', Icon: ViewModuleRounded },
];

// 10줄마다 페이지네이션 (목록형: 10개, 격자형 4열: 40개)
const ITEMS_PER_PAGE = { list: 10, grid: 40 };

const DEFAULT_TYPE = 'albums';
const DEFAULT_SORT = 'latest';
const TYPE_KEYS = SUB_TABS.map((t) => t.key);
const SORT_KEYS = SORT_OPTIONS.map((o) => o.value);

const ReviewTab = ({ api, userId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem('myReviewViewMode') || 'list',
  );

  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  // URL에서 상태 읽기
  const urlType = searchParams.get('type');
  const urlSort = searchParams.get('sort');
  const typeTab = TYPE_KEYS.includes(urlType) ? urlType : DEFAULT_TYPE;
  const sortBy = SORT_KEYS.includes(urlSort) ? urlSort : DEFAULT_SORT;
  const currentPage = parseInt(searchParams.get('page')) || 1;

  // xs에서는 항상 목록형
  const effectiveViewMode = isXs ? 'list' : viewMode;
  const itemsPerPage = ITEMS_PER_PAGE[effectiveViewMode];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await (userId ? api(userId) : api());
      setAllData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch review data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 카운트 계산
  const countByType = useMemo(
    () => ({
      albums: allData.filter((item) => item.album.albumType === 'ALBUM').length,
      singles: allData.filter((item) => item.album.albumType === 'SINGLE').length,
    }),
    [allData],
  );

  const subTabsWithCount = useMemo(
    () =>
      SUB_TABS.map((tab) => ({
        ...tab,
        label: `${tab.label} (${countByType[tab.key] ?? 0})`,
      })),
    [countByType],
  );

  // 필터링
  const filteredData = useMemo(() => {
    const typeFilter = typeTab === 'albums' ? 'ALBUM' : 'SINGLE';
    return allData.filter((item) => item.album.albumType === typeFilter);
  }, [allData, typeTab]);

  // 정렬
  const sortedData = useMemo(() => {
    if (sortBy === 'likes') {
      return [...filteredData].sort(
        (a, b) => (b.review.likeCount || 0) - (a.review.likeCount || 0),
      );
    }
    if (sortBy === 'rating_high') {
      return [...filteredData].sort((a, b) => b.review.rating - a.review.rating);
    }
    if (sortBy === 'rating_low') {
      return [...filteredData].sort((a, b) => a.review.rating - b.review.rating);
    }
    // latest: API 원본 순서
    return filteredData;
  }, [filteredData, sortBy]);

  // 페이지네이션
  const pageCount = useMemo(
    () => Math.ceil(sortedData.length / itemsPerPage),
    [sortedData, itemsPerPage],
  );

  const safePage = pageCount > 0 ? Math.min(currentPage, pageCount) : 1;

  const paginatedData = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, safePage, itemsPerPage]);

  const handleTypeTabChange = (newType) => {
    setSearchParams({ type: newType, sort: DEFAULT_SORT, page: '1' });
  };

  const handleSortChange = (newSort) => {
    setSearchParams({ type: typeTab, sort: newSort, page: '1' });
  };

  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    localStorage.setItem('myReviewViewMode', newMode);
    setSearchParams({ type: typeTab, sort: sortBy, page: '1' });
  };

  const handlePageChange = (event, value) => {
    setSearchParams({ type: typeTab, sort: sortBy, page: value.toString() });
  };

  return (
    <ProgressBar isLoading={loading}>
      {/* 서브탭 + 보기방식 + 정렬 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: '16px', md: '20px', lg: '30px' },
        }}
      >
        <SubTab tabs={subTabsWithCount} activeTab={typeTab} onTabChange={handleTypeTabChange} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <ViewModeToggle
            viewMode={viewMode}
            onChangeViewMode={handleViewModeChange}
            modes={VIEW_MODES}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          />
          <SelectFilter
            label="정렬"
            value={sortBy}
            onChange={handleSortChange}
            options={SORT_OPTIONS}
          />
        </Box>
      </Box>
      {paginatedData.length === 0 && !loading ? (
        <NoData />
      ) : (
        <>
          {effectiveViewMode === 'list' ? (
            <Stack spacing={'15px'}>
              {paginatedData.map((item, index) => (
                <React.Fragment key={item.review.id}>
                  <Box>
                    <MyReviewCard data={item} referrerType={REFERRER_TYPE.MYPAGE} />
                    <Divider sx={{ borderColor: theme.palette.grey.line, mt: '15px' }} />
                  </Box>
                </React.Fragment>
              ))}
            </Stack>
          ) : (
            <Box sx={{ overflow: 'hidden' }}>
              <Grid container columnSpacing={{ md: '16px', lg: '20px' }} rowSpacing={'40px'}>
                {paginatedData.map((item, index) => (
                  <React.Fragment key={item.review.id}>
                    <Grid item xs={12} md={3}>
                      <MyReviewVerticalCard data={item} referrerType={REFERRER_TYPE.MYPAGE} />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          )}

          {pageCount > 1 && (
            <Pagination count={pageCount} page={safePage} onChange={handlePageChange} />
          )}
        </>
      )}
    </ProgressBar>
  );
};

export default ReviewTab;
